/**
 * NovaCAD persistence — IndexedDB via Dexie.
 *
 * Everything lives client-side. Four stores:
 *
 *  projects   Document metadata + the serialized timeline (small: the
 *             parametric recipe, not geometry). Autosaved on every commit.
 *  assets     Content-addressed binary blobs: imported meshes (STL/OBJ/GLTF),
 *             asset-browser downloads, textures, environment maps. Keyed by
 *             hash so re-imports dedupe and ImportMeshParams stays a pointer.
 *  meshCache  Tessellation results keyed by feature content hash, so
 *             reopening a project shows geometry instantly and only stale
 *             features regenerate.
 *  settings   Key/value: AI endpoint config, UI prefs, recent projects.
 *             (The AI API key is stored here on the user's machine only —
 *             it is never sent anywhere except the endpoint the user set.)
 *
 * Safari notes: IndexedDB on iPadOS/macOS WebKit is solid on 15+, but
 * storage is evictable under pressure — we request persistent storage at
 * startup and surface the result in the UI.
 */

import Dexie, { type Table } from 'dexie';
import type { TimelineJSON } from '../timeline/timeline';

export interface ProjectRecord {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  timeline: TimelineJSON;
  /** Small JPEG data URL for the project browser. */
  thumbnail?: string;
}

export interface AssetRecord {
  /** Content hash of `data` (fnv1a64 of bytes) — dedupes re-imports. */
  hash: string;
  kind: 'mesh' | 'brep' | 'texture' | 'hdri' | 'thumbnail';
  mimeType: string;
  name: string;
  byteLength: number;
  data: Blob;
  createdAt: number;
}

export interface MeshCacheRecord {
  /** Feature content hash from the evaluator. */
  contentHash: string;
  projectId: string;
  positions: ArrayBuffer;
  normals: ArrayBuffer;
  indices: ArrayBuffer;
  edgePositions: ArrayBuffer;
  createdAt: number;
}

export interface SettingRecord {
  key: string;
  value: unknown;
}

export class NovaCadDB extends Dexie {
  projects!: Table<ProjectRecord, string>;
  assets!: Table<AssetRecord, string>;
  meshCache!: Table<MeshCacheRecord, string>;
  settings!: Table<SettingRecord, string>;

  constructor() {
    super('novacad');
    this.version(1).stores({
      // Only indexed fields are listed; full objects are stored regardless.
      projects: 'id, name, updatedAt',
      assets: 'hash, kind, createdAt',
      meshCache: 'contentHash, projectId',
      settings: 'key',
    });
  }
}

export const db = new NovaCadDB();

/**
 * Ask the browser not to evict our data. Safari grants this silently for
 * sites with engagement; failure is fine — we just warn in the UI.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) {
      return await navigator.storage.persist();
    }
  } catch {
    /* Safari private mode etc. */
  }
  return false;
}
